import { memo, useCallback } from "react";

import {
  CompletedIcon,
  FoodIcon,
  LocationIcon,
  DocumentIcon,
} from "../assets/images/svg";
import { logoBlack } from "../assets/images";
import { cn } from "../utils/tailwind";
import { WhatsappButton } from "../components/ui/button";
import { COMPLETED_STEP } from "../constants/Onboarding";

// Step indicator component
const StepIndicator = memo(({ currentStep, totalSteps, isFormDataLoading }) => {
  const getStepStatus = useCallback(
    (step) => {
      if (step < currentStep) return "completed";
      if (step === currentStep) return "current";
      return "upcoming";
    },
    [currentStep]
  );

  const getStepIcon = (step, status) => {
    if (status === "completed") return <CompletedIcon height={24} width={24} />;
    switch (step) {
      case 1:
        return <DocumentIcon height={24} width={24} />;
      case 2:
        return <LocationIcon height={24} width={24} />;
      case 3:
        return <FoodIcon height={24} width={24} />;
      default:
        return step;
    }
  };

  const getStepLabel = (step) => {
    switch (step) {
      case 1:
        return "Información general";
      case 2:
        return "Detalles de tu local";
      case 3:
        return "Productos";
      default:
        return "";
    }
  };

  const getStepColor = (status) => {
    if (status === "completed") return "bg-darkYellow text-white";
    if (status === "current") return "bg-neutralLight text-darkYellow";
    return "text-white bg-neutralMediumText";
  };

  return (
    <div className="w-full lg:w-1/4 h-[200px] lg:h-full flex-shrink-0 bg-yellow">
      {isFormDataLoading ? (
        <></>
      ) : (
        <>
          <div className="flex h-full lg:hidden">
            <div className="flex flex-col items-center w-full p-4 justify-center h-full">
              <img src={logoBlack} alt="logo" className="w-40" />
              <div className="w-full flex flex-row items-center gap-2 justify-center mt-0">
                {Array.from({ length: totalSteps }).map((_, idx) => {
                  const step = idx + 1;
                  const status = getStepStatus(step);
                  const color = getStepColor(status);
                  return (
                    <div
                      key={step}
                      className={`flex flex-a h-[110px] flow-row items-center ${
                        step === totalSteps
                          ? "justify-start"
                          : "justify-center w-1/3"
                      }`}
                    >
                      {/* Step icon */}
                      <div
                        className={cn(
                          "w-[2.25rem] h-[2.25rem] rounded-full flex items-center justify-center font-medium text-sm transition-all duration-300",
                          color
                        )}
                      >
                        <div
                          className={cn(
                            "w-6 h-6 flex items-center justify-center rounded-full",
                            color
                          )}
                        >
                          {getStepIcon(step, status)}
                        </div>
                      </div>
                      {/* Connector line */}
                      {step < totalSteps && (
                        <div className="flex flex-grow pl-2">
                          <div
                            className={`w-full h-1 rounded-full ${
                              status === "completed"
                                ? "bg-darkYellow"
                                : "bg-neutralMediumText"
                            }`}
                          />
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
            <div className="hidden lg:flex flex-col items-center justify-center gap-3 p-2 absolute bottom-2">
              <WhatsappButton text="¿Dudas? Chatea con nosotros" />
            </div>
          </div>
          <div className="hidden lg:flex mb-12 px-4 flex-col items-center justify-center h-full w-full bg-yellow relative ">
            <div className="flex flex-col space-y-8">
              <img src={logoBlack} alt="logoBlack" className="w-48" />
              {Array.from({ length: totalSteps }).map((_, idx) => {
                const step = idx + 1;
                const status = getStepStatus(step);
                const color = getStepColor(status);
                return (
                  <div key={step} className={`flex flex-row p-2`}>
                    {/* Connector line */}
                    {step < totalSteps ? (
                      <div
                        className={cn(
                          "relative left-[19px] top-10 w-[4px] h-full rounded-full z-100",
                          status === "completed"
                            ? "bg-darkYellow"
                            : "bg-neutralMediumText"
                        )}
                      />
                    ) : (
                      <div className="w-[4px]" />
                    )}
                    <div
                      className={cn(
                        "w-[2.25rem] h-[2.25rem] rounded-full flex items-center justify-center font-medium text-sm transition-all duration-300",
                        color
                      )}
                    >
                      <div
                        className={cn(
                          "w-6 h-6 flex items-center justify-center rounded-full",
                          color
                        )}
                      >
                        {getStepIcon(step, status)}
                      </div>
                    </div>

                    <div className="ml-4 py-1">
                      <h3
                        className={
                          "text-sm md:text-base font-semibold text-neutralStrongBlack"
                        }
                      >
                        {getStepLabel(step)}
                      </h3>
                      <span
                        className={
                          "font-medium text-sm md:text-base text-neutralDarkBlack"
                        }
                      >
                        Paso {step}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
            {currentStep < COMPLETED_STEP && (
              <div className="flex flex-col items-center justify-center gap-3 p-2 absolute bottom-2">
                <WhatsappButton text="¿Dudas? Chatea con nosotros" />
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
});

StepIndicator.displayName = "StepIndicator";

export default StepIndicator;
